import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfig } from "@/contexts/ConfigContext";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BACKEND_URL } from "@/config";

export default function MaintenancePage() {
  const { siteAccess } = useConfig();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [checking, setChecking] = useState(false);

  // Determine mode: "maintenance" (manual, admin set) vs "deploy" (backend unreachable)
  const isManualMaintenance = siteAccess?.mode === "maintenance";

  // Health check — only runs in deploy mode (b)
  const checkBackend = useCallback(async () => {
    if (isManualMaintenance) return;
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/health`, { cache: "no-store" });
      if (res.ok) {
        navigate("/", { replace: true });
      }
    } catch {
      // backend still down
    } finally {
      setChecking(false);
    }
  }, [isManualMaintenance, navigate]);

  // Auto-poll backend every 15s in deploy mode
  useEffect(() => {
    if (isManualMaintenance) return;
    const interval = setInterval(checkBackend, 15000);
    return () => clearInterval(interval);
  }, [isManualMaintenance, checkBackend]);

  return (
    <div className="min-h-screen hero-background relative flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" />

      <div className="relative z-10 max-w-lg w-full text-center space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            loading="lazy"
            src="/logo-mototrips.png"
            alt="MotoTrips"
            className="h-48 md:h-56 w-auto object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
          />
        </div>

        {/* Animated dots */}
        <div className="flex items-center justify-center gap-1.5">
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>

        {/* Message */}
        <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-white/10 space-y-1">
          <p className="text-white text-base leading-relaxed">
            {t("maintenance.message_line1")}
          </p>
          <p className="text-white text-sm leading-relaxed">
            {t("maintenance.message_line2")}
          </p>
        </div>

        {/* Auto-redirect notice (deploy mode only) */}
        {!isManualMaintenance && (
          <p className="text-white/40 text-xs">
            {t("maintenance.auto_refresh")}
          </p>
        )}

        {/* Deploy mode: auto-refresh notice */}
        {!isManualMaintenance && (
          <div className="flex items-center justify-center gap-2 text-white/50 text-xs">
            <RefreshCw className={`w-3 h-3 ${checking ? "animate-spin" : ""}`} />
            <span>{t("maintenance.checking")}</span>
          </div>
        )}

        {/* Refresh button */}
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
          className="bg-white/10 border-white/20 hover:bg-white/20 text-white"
          data-testid="maintenance-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {t("maintenance.refresh")}
        </Button>
      </div>
    </div>
  );
}
